import { Box, Button, CircularProgress } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useState } from "react";
import { useDownloadInvoicesZipMutation } from "Api/Mutations/Invoices/useDownloadInvoicesZipMutation";
import { useGetSettlementPeriodsQuery } from "Api/Queries/Transactions/useGetSettlementPeriodsQuery";
import { SettlementPeriodSelector } from "Components/Shared/SettlementPeriodSelector";
import type { SettlementInvoiceType } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { formatPeriodMonth } from "Utils/SettlementUtils";

const RESOURCES = Resources.Components.Invoice.BulkDownload;

type Props = {
  invoiceType: SettlementInvoiceType;
};

export const DownloadInvoicesSection = ({ invoiceType }: Props) => {
  const [selectedPeriodID, setSelectedPeriodID] = useState<number | null>(null);
  const { t } = useResource();
  const { mutate: download, isPending } = useDownloadInvoicesZipMutation();
  const { data: settlementPeriods = [] } = useGetSettlementPeriodsQuery();

  const handleDownload = () => {
    if (!selectedPeriodID) {
      return;
    }
    const period = settlementPeriods.find(
      (p) => p.settlementPeriodID === selectedPeriodID,
    );
    download({
      type: invoiceType,
      settlementPeriodId: selectedPeriodID,
      periodLabel: formatPeriodMonth(period?.recordSettlementDate),
    });
  };

  return (
    <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
      <SettlementPeriodSelector
        value={selectedPeriodID}
        onChange={setSelectedPeriodID}
        size="small"
      />
      <Button
        variant="contained"
        disabled={!selectedPeriodID || isPending}
        onClick={handleDownload}
        endIcon={
          isPending ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            <FileDownloadOutlinedIcon />
          )
        }
      >
        {t(RESOURCES.Download)}
      </Button>
    </Box>
  );
};
